import { useUpload } from '@components'
import { getFilesPageUrl } from '@root/constants'
import { useBusinessContext } from '@root/context'
import { useOnboardSteps, useQueryParam } from '@hooks'
import { IFile, createFolder, uploadFile, UploadFileVariables } from '@query'
import { showFeaturebase } from '@utils'
import React from 'react'
import { toast } from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { uriTransformer } from 'react-markdown/lib/uri-transformer.js'
import { Link, useHistory } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'

const APP_ACTION_PREFIX = 'action://'

// TODO: This could be changed to use a tag like solution in the future
enum AppAction {
  OPEN_CUSTOMER_SUPPORT = 'action://open_customer_support',
  CREATE_NEW_FOLDER = 'action://create_new_folder',
  UPLOAD_FILE = 'action://upload_file'
}

const useActionHandlers = () => {
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const [t] = useTranslation()
  const history = useHistory()

  const { mutate: createBlankFolder, isPending: isCreatingFolder } = useMutation({
    mutationFn: () => createFolder({ slug }, { name: t('files.actionBar.defaultName') }),
    onSuccess: data => {
      queryClient.invalidateQueries({ queryKey: [slug] })
      history.push(getFilesPageUrl(slug, { id: data.id, idType: 'folder' }))
    },
    onError: () => {
      toast.error(t('general.error'))
    }
  })

  return React.useMemo(
    () => ({
      [AppAction.OPEN_CUSTOMER_SUPPORT]: () => showFeaturebase('messages'),
      [AppAction.CREATE_NEW_FOLDER]: () => !isCreatingFolder && createBlankFolder()
    }),
    [isCreatingFolder, createBlankFolder]
  )
}

const UploadFileActionLink: React.FC<{
  children: React.ReactNode
  [key: string]: any
}> = ({ children, ...props }) => {
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const [t] = useTranslation()
  const { hasOpenAddFileStep } = useOnboardSteps()
  const [folderId] = useQueryParam<number>('folder', value =>
    value ? parseInt(value, 10) : undefined
  )

  const { mutateAsync: uploadFileMutation } = useMutation<IFile, unknown, UploadFileVariables>({
    mutationFn: ({ file, ...options }) =>
      uploadFile(file, { slug, fileName: file.name, folderId }, options),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [slug, 'files'] })
      if (hasOpenAddFileStep) {
        await queryClient.invalidateQueries({ queryKey: [slug], exact: true })
      }
    },
    onError: error => {
      const { status } = (error as any) ?? {}
      if (status == 426) {
        toast.error(t('error.upgradeRequired'))
      }
    }
  })

  const { open, getInputProps } = useUpload(
    (file, options) => uploadFileMutation({ file, ...options }),
    true,
    undefined,
    false,
    true
  )

  return (
    <>
      <input {...getInputProps()} />
      <a
        href="#"
        {...props}
        onClick={event => {
          event.preventDefault()
          open()
        }}
      >
        {children}
      </a>
    </>
  )
}

const ActionLink: React.FC<{
  href: string
  children: React.ReactNode
  [key: string]: any
}> = ({ href = '', children, ...props }) => {
  const actionHandlers = useActionHandlers()

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (href === AppAction.OPEN_CUSTOMER_SUPPORT || href === AppAction.CREATE_NEW_FOLDER) {
      actionHandlers[href]()
    }
  }

  if (href === AppAction.UPLOAD_FILE) {
    return <UploadFileActionLink {...props}>{children}</UploadFileActionLink>
  }

  // Handle action:// protocol
  if (href?.startsWith(APP_ACTION_PREFIX)) {
    return (
      <a href="#" onClick={handleClick} {...props}>
        {children}
      </a>
    )
  }

  // Handle internal app links
  const isAppLink = href.startsWith('/') && !href.startsWith('//')
  if (isAppLink) {
    return (
      <Link to={href} {...props}>
        {children}
      </Link>
    )
  }

  // Handle external links
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  )
}

export const MarkdownText: React.FC<{ text: string }> = ({ text }) => (
  <ReactMarkdown
    components={{
      a({ href = '', children, ...props }) {
        return (
          <ActionLink href={href} {...props}>
            {children}
          </ActionLink>
        )
      },
      p({ children, ...props }) {
        return (
          <p style={{ margin: 0 }} {...props}>
            {children}
          </p>
        )
      }
    }}
    // Preserve action:// protocol URLs (ReactMarkdown sanitizes them by default)
    transformLinkUri={uri => {
      // Allow action:// protocol to pass through
      if (uri.startsWith(APP_ACTION_PREFIX)) {
        return uri
      }
      // Use default transformation for other URIs
      // https://github.com/remarkjs/react-markdown/issues/373
      return uriTransformer(uri)
    }}
  >
    {text}
  </ReactMarkdown>
)
